import * as React from 'react';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import * as moment from 'moment';
import { sortBy, merge, find } from 'lodash';
import bookingStatuses from '../../data/bookingStatuses';
import { getBookingsWithStatus } from '../../redux/actions/bookings';

import {
  IconButton,
  Notification,
  Form,
  Tag,
  BookingItem,
  HeadingSection,
  RouteWrapper,
  StaggeredList,
  Section,
  Loader,
  Box,
} from '../../components';

interface Props {
  vehicleTypes: Array<any>;
  match: any;
  isFetching: boolean;
  dispatch: Function;
}

interface State {
  isReady: boolean;
  bookings: Array<any>;
  status: string;
  date: any;
  showHiresOnHold: boolean;
  hiresOnHoldCount: number;
  error: string | boolean;
  filterVehicleType?: number;
  interval: any;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    const status = this.props.match.params.status;
    this.state = {
      isReady: false,
      bookings: [],
      status: status ? status.toUpperCase() : '',
      date: this.props.match.params.period
        ? moment(this.props.match.params.period)
        : moment(),
      showHiresOnHold: false,
      hiresOnHoldCount: 0,
      error: false,
      interval: false,
    };
    this.getPageTitle = this.getPageTitle.bind(this);
    this.isScopedToDay = this.isScopedToDay.bind(this);
    this.isEndingToday = this.isEndingToday.bind(this);
  }

  /**
   * Fetch bookings and vehicle types on initial load
   */
  async componentDidMount() {
    await this.fetchBookings();
  }

  /**
   * Stop fetching for bookings on route exit
   */
  componentWillUnmount() {
    if (this.state.interval !== false) {
      window.clearInterval(this.state.interval);
    }
  }

  /**
   * Determine whether the current status should be scope to the day
   * @return {Boolean}
   */
  isScopedToDay() {
    return ['PENDING', 'COMPLETE'].indexOf(this.state.status) !== -1;
  }

  /**
   * Determine whether the current status should be scoped to finished today
   * @return {Boolean}
   */
  isEndingToday() {
    return ['ACTIVE'].indexOf(this.state.status) !== -1;
  }

  /**
   * Fetch respective bookings from the API
   * @param {Boolean?} ignoreInterval
   */
  async fetchBookings(ignoreInterval?: Boolean) {
    const response = await this.props.dispatch(
      getBookingsWithStatus(
        this.state.status,
        this.isScopedToDay() ? this.state.date : undefined,
        this.isEndingToday() ? this.state.date : undefined,
      ),
    );
    this.props.dispatch(
      replace(
        `/bookings/status/${this.state.status}/${this.state.date.format(
          'YYYY-MM-DD',
        )}`,
      ),
    );
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }

    this.setState({
      bookings: response.data,
      isReady: true,
      hiresOnHoldCount: response.data.filter(
        (booking) => booking.has_final_form,
      ).length,
    });

    // We automatically refresh bookings every 2 minutes
    // It is reset whenever the status is changed
    const REFRESH_INTERVAL = 1000 * 60 * 2;
    if (ignoreInterval === undefined) {
      if (this.state.interval !== false) {
        window.clearInterval(this.state.interval);
      }
      this.setState({
        interval: window.setInterval(() => {
          this.refreshBookings();
        }, REFRESH_INTERVAL),
      });
    }
  }

  /**
   * Refresh bookings
   * @param {Object?} newState
   */
  refreshBookings(newState: any = {}) {
    if (newState.status !== undefined) {
      this.props.dispatch(replace(`/bookings/status/${newState.status}`));
    }
    this.setState(merge(newState, { isLoading: true }), () => {
      this.fetchBookings();
    });
  }

  /**
   * Get the appropriate page title
   * @return {String}
   */
  getPageTitle(): string {
    const bookingStatus = find(bookingStatuses, { value: this.state.status });
    return bookingStatus.menuName || 'Bookings';
  }

  private renderVehicleTypeSelector = () => {
    return (
      <div className="mb">
        <Form
          key="vehicle_types"
          type="form"
          fields={[
            { name: 'vehicle_type', type: 'select', placeholder: 'Filter...' },
          ]}
          values={{
            vehicle_type: this.props.vehicleTypes.map((vehicleType) => ({
              key: vehicleType.id,
              name: vehicleType.name,
              value: vehicleType.id,
            })),
          }}
          disabled={this.props.isFetching}
          hasButton={false}
          handleChange={({ vehicle_type }) => {
            this.setState({
              filterVehicleType:
                vehicle_type === 'Filter...' ? undefined : vehicle_type,
            });
          }}
        />
      </div>
    );
  };

  private renderSectionControls = () => {
    const controls = [];
    if (this.isScopedToDay() || this.isEndingToday()) {
      controls.push(
        <IconButton
          key="prev"
          icon="chevron-left"
          className="is-default"
          handleClick={() => {
            const date = this.state.date.clone().subtract(1, 'days');
            this.setState({ date }, () => {
              this.fetchBookings();
            });
          }}
        >
          Previous Day
        </IconButton>,
        <IconButton
          key="next"
          icon="chevron-right"
          className="is-default"
          isRight
          handleClick={() => {
            const date = this.state.date.clone().add(1, 'days');
            this.setState({ date }, () => {
              this.fetchBookings();
            });
          }}
        >
          Next Day
        </IconButton>,
      );
    }
    if (this.state.status.toLowerCase() === 'on-hold') {
      if (this.state.hiresOnHoldCount === 0) {
        controls.push(
          <IconButton
            key="hires-on-hold-disabled"
            icon="eye-slash"
            className="is-static"
          >
            No Hires on Hold
          </IconButton>,
        );
      } else {
        controls.push(
          <IconButton
            key="hires-on-hold"
            handleClick={() =>
              this.setState({ showHiresOnHold: !this.state.showHiresOnHold })
            }
            icon={!this.state.showHiresOnHold ? 'eye-slash' : 'eye'}
            className={`is-${
              !this.state.showHiresOnHold ? 'default' : 'danger'
            }`}
          >
            Hires on Hold &nbsp;
            <Tag type="dark">{this.state.hiresOnHoldCount}</Tag>
          </IconButton>,
        );
      }
    }
    controls.push(this.renderVehicleTypeSelector());
    return controls;
  };

  private get filteredBookings() {
    const bookings = this.state.bookings
      .filter(
        (booking) =>
          this.state.filterVehicleType === undefined ||
          Number(booking.vehicle_type_id) ===
            Number(this.state.filterVehicleType),
      )
      .filter(
        (booking) =>
          booking.status.toLowerCase() !== 'on-hold' ||
          booking.has_final_form === this.state.showHiresOnHold,
      )
      .sort((a, b) => {
        if (a.vehicle_type.size !== b.vehicle_type.size) {
          return a.vehicle_type.size > b.vehicle_type.size ? 1 : -1;
        }
        if (this.state.status === 'UNASSIGNED') {
          return moment(a.collection_date).isAfter(moment(b.collection_date))
            ? -1
            : 1;
        }
        if (this.state.status === 'COMPLETE') {
          return moment(a.completed_at).isAfter(moment(b.completed_at))
            ? -1
            : 1;
        }
        if (moment(a.start).isAfter(moment(b.start))) {
          return 1;
        }
        if (moment(a.start).isBefore(moment(b.start))) {
          return -1;
        }
        return 0;
      });

    return bookings;
  }

  /**
   * Render a list of bookings
   */
  render() {
    let currentDay = moment();
    if (this.isScopedToDay() || this.isEndingToday()) {
      currentDay = this.state.date;
    }

    if (!this.state.isReady) {
      return (
        <Section>
          <Loader fullwidth />
        </Section>
      );
    }

    return (
      <RouteWrapper title={this.getPageTitle()} error={this.state.error}>
        <HeadingSection
          title={this.getPageTitle()}
          subtitle={moment(currentDay).format('dddd Do MMMM')}
          controls={this.renderSectionControls()}
        />
        <Section>
          <div className="columns">
            <div className="column is-4-desktop is-3-widescreen">
              <Box>
                <aside className="menu">
                  <ul className="menu-list">
                    {bookingStatuses
                      .filter(({ value }) => value !== 'CANCELLED')
                      .map(({ menuName, value }) => (
                        <li key={value}>
                          <a
                            className={
                              this.state.status === value ? 'is-active' : ''
                            }
                            onClick={() => {
                              this.props.dispatch(
                                replace(
                                  `/bookings/status/${value}/${currentDay.format(
                                    'YYYY-MM-DD',
                                  )}`,
                                ),
                              );
                              this.setState(
                                { status: value },
                                this.fetchBookings,
                              );
                            }}
                          >
                            {menuName}
                          </a>
                        </li>
                      ))}
                  </ul>
                </aside>
              </Box>
            </div>
            <div className="column is-8-desktop is-9-widescreen">
              {this.props.isFetching ? (
                <Loader fullwidth />
              ) : (
                <>
                  {this.filteredBookings.length === 0 && (
                    <Notification type="default">
                      No bookings found
                    </Notification>
                  )}
                  <StaggeredList>
                    {this.filteredBookings.map((booking) => (
                      <BookingItem
                        key={booking.id}
                        booking={booking}
                        handleUpdate={() => {
                          this.refreshBookings();
                        }}
                        currentDay={currentDay}
                      />
                    ))}
                  </StaggeredList>
                </>
              )}
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ bookings, vehicleTypes }) => ({
  isFetching: bookings.isFetching,
  vehicleTypes: sortBy(vehicleTypes.vehicleTypes, ['size', 'id']),
});

export default connect(mapStateToProps)(Route);
